import type { ReactNode } from 'react';
import { NavLink, useMatch, useResolvedPath } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import { CaretDown, PushPin, PushPinSlash } from '@phosphor-icons/react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { listOpenSuggestions } from '@/api/automation';
import {
  DEEP_NAV,
  HUB_NAV,
  PRIMARY_NAV,
  TRACKABLE_NAV,
  type NavItem,
} from './nav-items';
import type { RecentNav } from './useRecentNav';

// Lightweight nav badge — count + pre-translated accessible label, prepared by
// the parent so NavRow stays a presentation component. Today only the
// Assistant entry uses it; widen the producer when other items need it.
interface NavBadge {
  count: number;
  label: string;
}

interface Props {
  slug: string;
  collapsed: boolean;
  // Recent + pinned state, owned by DashboardShell so the push-on-route
  // effect and the read here see the same in-memory instance.
  recentNav: RecentNav;
  // Opens the SettingsPanel slide-over. Wired from DashboardShell's
  // settings state so the sidebar Settings row toggles the same panel
  // that the gear icon in the top bar does.
  onOpenSettings: () => void;
  // Called after a nav item is clicked — used by the mobile sheet to close
  // itself once a destination is picked.
  onNavigate?: (() => void) | undefined;
}

// SidebarNav is the Stripe-style three-section sidebar:
//   1. Primary  — always-visible top items (Home, Members, Teams, Schedule).
//   2. Recently visited — accordion. Pinned items first, then most-recent
//      visits. ONLY surfaces DEEP_NAV sub-pages (Settings → Log /
//      Administrators / Danger zone, etc.) — hub pages already have a
//      permanent home in "Alla sidor" and would just duplicate.
//   3. All pages — accordion. Top-level "main" pages only (Website,
//      Settings). Sub-pages NEVER appear here — they only surface via
//      Recently visited after a real visit. The Settings row opens the
//      panel rather than navigating; the user picks a sub-page from
//      there.
//
// When the sidebar is collapsed (icon rail), only the Primary section
// renders — the accordions don't make sense without label width.
export function SidebarNav({ slug, collapsed, recentNav, onOpenSettings, onNavigate }: Props) {
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const base = `/orgs/${slug}`;

  // Items to show in "Recently visited". Pinned first (in pin order),
  // then chronological recents. Items without a matching definition
  // (e.g. an old ID after a refactor) are filtered out defensively.
  const recentItems = recentNav.ordered
    .map((id) => TRACKABLE_NAV.find((i) => i.id === id))
    .filter((i): i is NavItem => i != null);

  // Open assistant suggestions drive the badge on the Assistant entry.
  // Same query key as the Assistant page so reject/restore/approve there
  // invalidates this cache instantly. Endpoint is org-admin-only; non-admins
  // get a 403 → suggestionsQuery.data stays undefined → badge hidden.
  const suggestionsQuery = useQuery({
    queryKey: ['assistant-suggestions', 'open', slug],
    queryFn: () => listOpenSuggestions(token, slug),
    enabled: !!token && !!slug,
    retry: false,
    staleTime: 60_000,
  });
  const openSuggestionCount = suggestionsQuery.data?.length ?? 0;

  const badgeFor = (id: string): NavBadge | undefined => {
    if (id !== 'assistant' || openSuggestionCount === 0) return undefined;
    return {
      count: openSuggestionCount,
      label: t('nav.assistantBadgeAria', { count: openSuggestionCount }),
    };
  };

  // Collapsed rail: render all three sections as a flat icon list with
  // subtle separators. No section headers (no room for the eyebrow
  // text), no pin buttons (no room either — pin/unpin from expanded
  // view). Tooltips on each row carry the label so the user still knows
  // what they're hovering.
  if (collapsed) {
    return (
      <nav aria-label={t('nav.label')} className="flex flex-col gap-0.5">
        {PRIMARY_NAV.map((item) => (
          <NavRow
            key={item.id}
            item={item}
            base={base}
            collapsed
            onNavigate={onNavigate}
            onOpenSettings={onOpenSettings}
            translate={t}
            badge={badgeFor(item.id)}
          />
        ))}
        {recentItems.length > 0 && (
          <>
            <Divider />
            {recentItems.map((item) => (
              <NavRow
                key={item.id}
                item={item}
                base={base}
                collapsed
                onNavigate={onNavigate}
                onOpenSettings={onOpenSettings}
                    translate={t}
                badge={badgeFor(item.id)}
              />
            ))}
          </>
        )}
        <Divider />
        {HUB_NAV.map((item) => (
          <NavRow
            key={item.id}
            item={item}
            base={base}
            collapsed
            onNavigate={onNavigate}
            onOpenSettings={onOpenSettings}
            translate={t}
            badge={badgeFor(item.id)}
          />
        ))}
      </nav>
    );
  }

  return (
    <nav aria-label={t('nav.label')} className="flex flex-col gap-2">
      <div className="flex flex-col gap-0.5">
        {PRIMARY_NAV.map((item) => (
          <NavRow
            key={item.id}
            item={item}
            base={base}
            collapsed={false}
            onNavigate={onNavigate}
            onOpenSettings={onOpenSettings}
            translate={t}
            badge={badgeFor(item.id)}
          />
        ))}
      </div>

      <Accordion
        type="multiple"
        defaultValue={['recent', 'all']}
        className="flex flex-col gap-0.5"
      >
        <AccordionItem value="recent" className="border-0">
          <SectionTrigger label={t('nav.section.recent')} />
          <AccordionContent className="pt-1 pb-0">
            {recentItems.length === 0 ? (
              <p className="px-2.5 py-2 text-xs text-muted-foreground">
                {t('nav.empty.recents')}
              </p>
            ) : (
              <div className="flex flex-col gap-0.5 pl-3">
                {recentItems.map((item) => (
                  <NavRow
                    key={item.id}
                    item={item}
                    base={base}
                    collapsed={false}
                    onNavigate={onNavigate}
                    onOpenSettings={onOpenSettings}
                            translate={t}
                    badge={badgeFor(item.id)}
                    pinControl={{
                      pinned: recentNav.isPinned(item.id),
                      toggle: () => recentNav.togglePin(item.id),
                    }}
                  />
                ))}
              </div>
            )}
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="all" className="border-0">
          <SectionTrigger label={t('nav.section.all')} />
          <AccordionContent className="pt-1 pb-0">
            <div className="flex flex-col gap-0.5">
              {HUB_NAV.map((item) => (
                <NavRow
                  key={item.id}
                  item={item}
                  base={base}
                  collapsed={false}
                  onNavigate={onNavigate}
                  onOpenSettings={onOpenSettings}
                  translate={t}
                  badge={badgeFor(item.id)}
                />
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </nav>
  );
}

// Subtle horizontal rule used between sections in the collapsed (icon
// rail) sidebar. Width is reduced to ~50% with horizontal margin so it
// reads as a divider, not a hard cut. Only used in collapsed mode — the
// expanded view uses Accordion section headers instead.
function Divider() {
  return <div className="my-1 h-px self-center w-8 bg-border" aria-hidden />;
}

// Section trigger uses the small uppercase eyebrow style from the design
// system rather than the default Accordion heading look — the top-level
// rows are the visual anchor; section headings are quiet metadata.
function SectionTrigger({ label }: { label: string }) {
  return (
    <AccordionTrigger
      className={cn(
        'group flex w-full items-center justify-between px-2.5 py-1.5',
        'text-[11px] font-semibold uppercase tracking-wider text-muted-foreground',
        'hover:bg-transparent hover:no-underline hover:text-foreground/80',
        '[&>svg]:hidden', // hide the default trigger caret; we render our own
      )}
    >
      <span>{label}</span>
      <CaretDown className="h-3 w-3 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180" />
    </AccordionTrigger>
  );
}

interface NavRowProps {
  item: NavItem;
  base: string;
  collapsed: boolean;
  onNavigate?: (() => void) | undefined;
  onOpenSettings: () => void;
  translate: (key: string) => string;
  // Notification badge — when present and count > 0, draws a small pill at
  // the row's right edge (expanded) or top-right of the icon (collapsed).
  // The parent computes both count and a pre-translated aria label.
  badge?: NavBadge | undefined;
  pinControl?: {
    pinned: boolean;
    toggle: () => void;
  };
}

// Caps a notification count at 99+ so the pill stays narrow at any value.
function formatBadgeCount(n: number): string {
  return n > 99 ? '99+' : String(n);
}

// NavRow renders a single navigation entry. Two variants:
//   - link: regular <NavLink> for items with a `to` route.
//   - action: <button> for items with `opensSettings` — visually identical
//     to a NavLink, but onClick toggles a panel instead of navigating. The
//     "active" state is computed manually based on the current path so the
//     row highlights when the user is on any of the action's sub-pages
//     (e.g. Settings highlights while on /settings/*).
//
// Visual density is below the previous design (h-4 icons, 13px font,
// py-1.5 padding) but the click target stays at min-h-[var(--tap)] (44px)
// per CLAUDE.md WCAG 2.5.5 floor. The pin button (when supplied) is an
// absolutely-positioned chip on the right, hidden until row hover or
// when the item is already pinned.
function NavRow({
  item,
  base,
  collapsed,
  onNavigate,
  onOpenSettings,
  translate,
  badge,
  pinControl,
}: NavRowProps) {
  const Icon = item.icon;
  const label = translate(item.i18nKey);

  const rowClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      // `relative` anchors the collapsed-mode badge, which uses absolute
      // positioning to overhang the icon's top-right corner.
      'relative flex items-center font-heading text-[13px] font-semibold transition-colors',
      // Desktop-only sidebar: rail rows drop below the 44px WCAG touch floor
      // intentionally (32px / h-8). Mobile uses a different sheet UX.
      collapsed
        ? 'mx-auto h-8 w-8 justify-center rounded-full'
        : 'min-h-8 gap-2.5 rounded-pill px-2.5 py-1',
      // Reserve room on the right for the pin button so the label
      // doesn't shift when it appears on hover.
      !collapsed && pinControl ? 'pr-9' : '',
      isActive
        ? 'bg-primary text-primary-foreground'
        : 'text-foreground/80 hover:bg-muted hover:text-foreground',
    );

  // Resolve the link's active state eagerly, and unconditionally — these
  // hooks run before the action-item early return below so they're called
  // in the same order on every render (rules-of-hooks). For action items
  // `to` is just `base` and `match` is ignored.
  //
  // Why eager instead of NavLink's render-prop className: Radix's
  // <TooltipTrigger asChild> wraps NavLink with a Slot whose mergeProps
  // stringifies any function className via `[...].filter(Boolean).join(' ')`
  // — the function's source code ends up as the DOM className and every
  // Tailwind class is lost. Resolving to a static string here keeps the
  // row's styles intact in collapsed mode (where Slot is in play) while
  // still tracking the route.
  const to = `${base}${item.to ?? ''}`;
  const resolved = useResolvedPath(to);
  const match = useMatch({ path: resolved.pathname, end: item.to === '' });
  const isActive = match !== null;
  const linkClassName = rowClass({ isActive });

  // Badge node — same JSX in both link and button branches. Lime by default;
  // when the row itself is active (already lime), swap to ink-on-light so the
  // pill stays visible against the active background. Hidden from the
  // accessibility tree via the wrapping `aria-label` semantics on the link.
  const badgeNode = badge && (
    <span
      role="status"
      aria-label={badge.label}
      className={cn(
        'pointer-events-none inline-flex items-center justify-center rounded-full font-heading font-bold leading-none tabular-nums',
        collapsed
          ? 'absolute -top-1 -right-1 h-4 min-w-4 px-1 text-[10px]'
          : 'ml-auto h-5 min-w-5 px-1.5 text-[11px]',
        isActive
          ? 'bg-background text-foreground'
          : 'bg-primary text-primary-foreground',
      )}
    >
      {formatBadgeCount(badge.count)}
    </span>
  );

  // Action item — renders as a button. Currently the Settings row is the
  // only one. Buttons never carry an "active" state because they don't
  // own a route: highlighting Settings while any /settings/* sub-page is
  // open creates visual confusion (the user already sees the actual
  // sub-page row highlighted in Recently visited). The button is "the
  // way to open the panel", not "where you currently are".
  if (item.opensSettings) {
    return (
      <div className="group/nav relative">
        <CollapsedTooltip label={label} enabled={collapsed}>
          <button
            type="button"
            onClick={() => {
              onOpenSettings();
              onNavigate?.();
            }}
            aria-label={collapsed ? label : undefined}
            className={cn(rowClass({ isActive: false }), 'w-full text-left')}
          >
            <Icon className="h-4 w-4 shrink-0" />
            {!collapsed && <span className="truncate">{label}</span>}
            {badgeNode}
          </button>
        </CollapsedTooltip>
      </div>
    );
  }

  // Link item — standard NavLink. `to`/`isActive` were computed above so
  // the hooks stay above the action-item early return.
  return (
    <div className="group/nav relative">
      <CollapsedTooltip label={label} enabled={collapsed}>
        <NavLink
          to={to}
          end={item.to === ''}
          onClick={() => onNavigate?.()}
          aria-label={collapsed ? label : undefined}
          className={linkClassName}
        >
          <Icon className="h-4 w-4 shrink-0" />
          {!collapsed && <span className="truncate">{label}</span>}
          {badgeNode}
        </NavLink>
      </CollapsedTooltip>

      {pinControl && !collapsed && (
        <button
          type="button"
          onClick={pinControl.toggle}
          aria-label={pinControl.pinned ? translate('nav.unpin') : translate('nav.pin')}
          title={pinControl.pinned ? translate('nav.unpin') : translate('nav.pin')}
          className={cn(
            'absolute right-1.5 top-1/2 -translate-y-1/2',
            'flex h-7 w-7 items-center justify-center rounded-full',
            'text-foreground/60 transition-opacity',
            'hover:bg-background hover:text-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            // Pinned items keep the icon visible (so the user can tell
            // they're pinned at a glance). Unpinned: hover-only.
            pinControl.pinned
              ? 'opacity-100'
              : 'opacity-0 group-hover/nav:opacity-100 focus-visible:opacity-100',
          )}
        >
          {pinControl.pinned ? (
            <PushPinSlash className="h-3.5 w-3.5" weight="fill" />
          ) : (
            <PushPin className="h-3.5 w-3.5" />
          )}
        </button>
      )}
    </div>
  );
}

// CollapsedTooltip wraps a nav row with a Radix tooltip when the
// sidebar is in icon-rail mode. Portal-rendered so it escapes the
// `overflow-y-auto` scroll container the rail lives inside; when
// `enabled` is false (expanded sidebar) it returns the child untouched
// so there's no wrapper cost on the common path.
function CollapsedTooltip({
  label,
  enabled,
  children,
}: {
  label: string;
  enabled: boolean;
  children: ReactNode;
}) {
  if (!enabled) return <>{children}</>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

// Re-export tiers so DashboardShell's route tracker can find DEEP_NAV
// entries when the user lands on /settings/* sub-pages.
export { DEEP_NAV };
